import React, { PureComponent } from 'react'
import { reports } from 'gipsy-api'
import moment from 'moment'
import styled from 'styled-components'
import { Doughnut, Bar } from 'react-chartjs-2'
import { styles, utils, translations } from 'gipsy-misc'
import WeekLabel from 'features/weekLabel'
import { Tag } from 'gipsy-ui'
import cloneDeep from 'lodash/cloneDeep'

const uncategorized = {
  name: 'Uncategorized',
  backgroundColor: styles.colors.lightGrey,
  color: styles.colors.darkGrey
}

const eventsCategory = {
  name: '📆 Meetings',
  backgroundColor: '#F2F1F5',
  color: '#ADABBC'
}

export default class ReportsPage extends PureComponent {
  state = {
    date: moment().format('YYYY-MM-DD'),
    report: {}
  }

  componentDidMount = () => {
    this.load(this.state.date)
  }

  load = async date => {
    try {
      const report = await reports.get(date)

      this.setState({ report, date: date })
    } catch (err) {
      console.error(err)
    }
  }

  makeDonutChartProps = () => {
    const donutChartData = this.makeDonutChartData()

    const donutChartLegend = {
      display: true,
      position: 'left',
      labels: {
        boxWidth: 25,
        fontSize: 15,
        fontColor: styles.colors.textRegularColor,
        generateLabels: this.generateDonutChartLabels
      }
    }

    const donutChartOptions = {
      layout: {
        padding: {
          left: 0,
          right: 0,
          top: 25,
          bottom: 25
        }
      },
      tooltips: {
        displayColors: false,
        callbacks: {
          title: (tooltipItem, data) => {
            const label = data.labels[tooltipItem[0].index]
            return label
          },
          label: (tooltipItem, data) => {
            const duration = data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index]
            return utils.datetime.beautifyDuration(duration)
          }
        }
      },
      borderWidth: 10
    }

    return { donutChartOptions, donutChartLegend, donutChartData }
  }

  makeDonutChartData = () => {
    const { report } = this.state

    let donutChartTagData = []
    let donutChartTagLabels = []
    let donutChartTagBackgroundColor = []

    donutChartTagData.push(report.eventReport.workDuration)
    donutChartTagLabels.push(eventsCategory.name)
    donutChartTagBackgroundColor.push(report.eventReport.backgroundColor)

    if (report.byTags) {
      for (let activity of report.byTags) {
        donutChartTagData.push(activity.workDuration)

        const tag = activity.tag ? activity.tag : uncategorized
        donutChartTagLabels.push(tag.name)
        donutChartTagBackgroundColor.push(tag.backgroundColor)
      }
    }

    return {
      labels: donutChartTagLabels,
      datasets: [
        {
          data: donutChartTagData,
          backgroundColor: donutChartTagBackgroundColor
        }
      ]
    }
  }

  generateDonutChartLabels = ({ data }) => {
    const labels = data.labels
    const totalDuration = data.datasets[0].data.reduce((val, currentVal) => val + currentVal)
    const percentages = data.datasets[0].data.map(duration => {
      const percentage = totalDuration !== 0 ? ((duration / totalDuration) * 100).toFixed(0) : 0
      return percentage.toString() + '%'
    })

    let legendLabels = []
    for (let i = 0; i < labels.length; i++) {
      let label = labels[i]

      if (totalDuration !== 0) {
        label += ' - ' + percentages[i]
      }
      if (utils.datetime.beautifyDuration(data.datasets[0].data[i])) {
        label += ' - ' + utils.datetime.beautifyDuration(data.datasets[0].data[i])
      }

      legendLabels.push({
        text: label,
        lineWidth: 0,
        fillStyle: data.datasets[0].backgroundColor[i]
      })
    }
    return legendLabels
  }

  makeBarChartProps = () => {
    const barChartData = this.makeBarChartData()

    const barChartLegend = {
      display: false
    }

    const barChartOptions = {
      layout: {
        padding: {
          left: 25,
          right: 25,
          top: 25,
          bottom: 25
        }
      },
      scales: {
        xAxes: [
          {
            gridLines: {
              display: false
            },
            stacked: true
          }
        ],
        yAxes: [
          {
            gridLines: {
              borderDash: [10, 15],
              drawTicks: false
            },
            ticks: {
              padding: 5,
              callback: function(value) {
                return utils.datetime.beautifyDuration(value)
              },
              stepSize: 60 * 60 * 1000000000,
              min: 0
            },
            stacked: true
          }
        ]
      },
      tooltips: {
        displayColors: false,
        callbacks: {
          title: (tooltipItem, data) => {
            const label = data.labels[tooltipItem[0].index]
            return label
          },
          label: (tooltipItem, data) => {
            const duration = data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index]
            return data.datasets[tooltipItem.datasetIndex].label + ' - ' + utils.datetime.beautifyDuration(duration)
          }
        }
      }
    }

    return { barChartOptions, barChartLegend, barChartData }
  }

  makeBarChartData = () => {
    const { report } = this.state

    let barChartLabels = []
    let currentDate = report.firstDate
    while (!utils.datetime.isAfter(currentDate, report.lastDate)) {
      barChartLabels.push(moment(currentDate).format('ddd DD'))
      currentDate = moment(currentDate)
        .add(1, 'days')
        .format('YYYY-MM-DD')
    }

    let barChartDatasets = []
    if (report.byTags) {
      for (let tagActivity of report.byTags) {
        const tag = tagActivity.tag ? tagActivity.tag : uncategorized

        barChartDatasets.push({
          data: tagActivity.dailyDurations,
          label: tag.name,
          backgroundColor: tag.backgroundColor
        })
      }
    }

    barChartDatasets.push({
      data: report.eventReport.dailyDurations,
      label: 'Events',
      backgroundColor: report.eventReport.backgroundColor
    })

    return {
      labels: barChartLabels,
      datasets: barChartDatasets
    }
  }

  renderHeader = () => {
    return (
      <Header>
        <h1>{translations.reports.title}</h1>

        <WeekLabelWrapper
          firstDate={this.state.report.firstDate}
          lastDate={this.state.report.lastDate}
          onClickLeft={this.previousWeek}
          onClickRight={this.nextWeek}
        />
      </Header>
    )
  }

  previousWeek = () => {
    const nextDateStr = moment(this.state.date)
      .add(-7, 'days')
      .format('YYYY-MM-DD')

    this.load(nextDateStr)
  }

  nextWeek = () => {
    const nextDate = moment(this.state.date).add(7, 'days')
    if (nextDate.isAfter(moment())) {
      return
    }

    this.load(nextDate.format('YYYY-MM-DD'))
  }

  renderCategories = () => {
    const { report } = this.state

    let activities = cloneDeep(report.byTags)
    activities.push({
      tag: {
        name: eventsCategory.name,
        backgroundColor: report.eventReport.backgroundColor,
        color: 'white'
      },
      ...report.eventReport
    })

    return (
      <CategoriesContainer style={{ marginBottom: '15px' }}>
        {activities.map(activity => {
          const category = activity.tag ? activity.tag : uncategorized
          const name = category.name
          const duration = activity.workDuration !== 0 ? utils.datetime.beautifyDuration(activity.workDuration) : null
          const label = duration ? name + ' - ' + duration : name
          const color = category.color

          return (
            <Tag
              key={label}
              name={label}
              color={color}
              mode="preview"
              backgroundColor={category.backgroundColor}
              style={{ marginRight: '10px', marginBottom: '10px' }}
            />
          )
        })}
      </CategoriesContainer>
    )
  }

  render() {
    const { report } = this.state

    if (Object.keys(report).length === 0) {
      return <div id="reports-page">{this.renderHeader()}</div>
    }

    const totalDuration = report.workDuration
    const totalDurationLabel = utils.datetime.beautifyDuration(totalDuration) || '0h'

    const { donutChartOptions, donutChartLegend, donutChartData } = this.makeDonutChartProps()
    const { barChartOptions, barChartLegend, barChartData } = this.makeBarChartProps()

    return (
      <div id="reports-page">
        {this.renderHeader()}
        <Section style={{ marginTop: '30px' }}>{this.renderCategories()}</Section>
        <Section>
          <ChartContainer className="bar-container" style={{ marginBottom: '15px' }}>
            <Bar data={barChartData} legend={barChartLegend} options={barChartOptions} />
          </ChartContainer>
        </Section>

        <Section style={{ marginBottom: '20px' }}>
          {report.byTags && (
            <ChartContainer className="pie-container" style={{ marginRight: '15px' }}>
              <Doughnut data={donutChartData} legend={donutChartLegend} options={donutChartOptions} />
            </ChartContainer>
          )}

          <CardColumn>
            <Card style={{ marginBottom: '30px' }}>
              <CardTitle>{'🚀 ' + report.numTasksCompleted}</CardTitle>
              <br />
              <CardSubtitle>{translations.reports.numTasksCompletedLabel}</CardSubtitle>
            </Card>
            <Card>
              <CardTitle>{'⏰ ' + totalDurationLabel}</CardTitle>
              <br />
              <CardSubtitle>{translations.reports.hoursSpentOnWorkLabel}</CardSubtitle>
            </Card>
          </CardColumn>
        </Section>
      </div>
    )
  }
}

const Section = styled.div`
  display: flex;
`

const CategoriesContainer = styled.div`
  background-color: white;
  padding: 25px;
  display: flex;
  flex-wrap: wrap;
  flex-grow: 1;
  border-radius: 8px;
`

const ChartContainer = styled.div`
  background-color: white;
  flex-grow: 1;
  border-radius: 8px;
`

const Card = styled.div`
  background-color: white;
  padding: 25px;
  height: 50%;
  border-radius: 8px;
`

const CardTitle = styled.span`
  font-weight: bold;
  font-size: 38px;
  white-space: nowrap;
`

const CardSubtitle = styled.span`
  font-size: 14px;
  color: ${styles.colors.textLightColor};
`

const CardColumn = styled.div`
  display: flex;
  flex-direction: column;
`

const Header = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  align-items: center;
  height: 40px;
  color: ${styles.colors.textDarkColor};
  font-weight: 500;
`

const WeekLabelWrapper = styled(WeekLabel)`
  justify-content: flex-end;
`



// WEBPACK FOOTER //
// ./src/pages/reports/index.js